<template>
  <div class="reporting-section" v-if="visible">
    <div class="section-header">
      <i class="material-icons section-icon">link</i>
      <h2>{{ $t('dash.reporting.section.short_links.title') }}</h2>
    </div>
    <p class="section-subtitle">{{ $t('dash.reporting.section.short_links.subtitle') }}</p>

    <div class="metric-row">
      <MetricCard
        :value="data.clicks || 0"
        :previous-value="data.previous_clicks != null ? data.previous_clicks : null"
        :label="$t('dash.reporting.short_links.clicks')"
        :help="$t('dash.reporting.tooltip.short_links_clicks')"
        icon="touch_app"
      />
      <MetricCard
        :value="data.bot_clicks || 0"
        :previous-value="data.previous_bot_clicks != null ? data.previous_bot_clicks : null"
        :label="$t('dash.reporting.short_links.bot_clicks')"
        :help="$t('dash.reporting.tooltip.short_links_bot_clicks')"
        icon="fa-solid fa-robot"
      />
      <MetricCard
        :value="data.links_count || 0"
        :label="$t('dash.reporting.short_links.links_count')"
        :help="$t('dash.reporting.tooltip.short_links_links_count')"
        icon="link"
      />
    </div>

    <template v-if="graphData.length && labels.length">
      <div class="graph-title">{{ $t('dash.reporting.short_links.by_day') }}</div>
      <div class="graph-wrap">
        <Graph
          :id="`short-links-${graphId}`"
          :data="graphData"
          :labels="labels"
          :stacked="false"
          height="220px"
          width="100%"
        />
      </div>
    </template>

    <!-- Répartition des clics (humains) par type de publication ; part des liens publiés en commentaire -->
    <template v-if="typeItems.length">
      <div class="graph-title">{{ $t('dash.reporting.short_links.by_post_type') }}</div>
      <div class="sl-types">
        <MiniDonut :items="typeItems" :size="140" />
        <p v-if="commentClicks" class="sl-comment-share">
          <span class="sl-badge">{{ $t('dash.reporting.short_links.comment') }}</span>
          {{ $t('dash.reporting.short_links.comment_share', { clicks: commentClicks, share: commentShare }) }}
        </p>
      </div>
    </template>

    <template v-if="topLinks.length">
      <div class="graph-title">{{ $t('dash.reporting.short_links.top_links') }}</div>
      <div class="sl-table">
        <div class="sl-row sl-head">
          <span>{{ $t('dash.reporting.short_links.publication') }}</span>
          <span>{{ $t('dash.reporting.short_links.type') }}</span>
          <span>{{ $t('dash.reporting.short_links.link') }}</span>
          <span>{{ $t('dash.reporting.short_links.destination') }}</span>
          <span class="sl-num">{{ $t('dash.reporting.short_links.clicks') }}</span>
          <span class="sl-num">{{ $t('dash.reporting.short_links.bot_clicks') }}</span>
        </div>
        <div class="sl-row" v-for="link in topLinks" :key="link.code">
          <!-- Publication du lien : sur le réseau si l'URL est constructible, sinon dans Mashup -->
          <a v-if="link.publication && networkUrl(link)" class="sl-pub" :href="networkUrl(link)" target="_blank" rel="noopener"
            :title="$t('dash.reporting.short_links.open_network')">{{ publicationTitle(link) }}</a>
          <a v-else-if="link.publication && link.news_id" class="sl-pub" href="#" @click.prevent="openInMashup(link)"
            :title="$t('dash.reporting.short_links.open_mashup')">{{ publicationTitle(link) }}</a>
          <span v-else class="sl-pub muted">—</span>
          <span class="sl-type">
            {{ typeLabel(link.post_type) }}
            <span v-if="link.placement === 'comment'" class="sl-badge" :title="$t('dash.reporting.short_links.comment_tooltip')">{{ $t('dash.reporting.short_links.comment') }}</span>
          </span>
          <a class="sl-short" :href="shortUrl(link)" target="_blank" rel="noopener">{{ shortUrl(link).replace(/^https?:\/\//, '') }}</a>
          <a class="sl-dest" :href="link.url" target="_blank" rel="noopener" :title="link.url">{{ link.url }}</a>
          <span class="sl-num">{{ link.clicks || 0 }}</span>
          <span class="sl-num muted">{{ link.bot_clicks || 0 }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import MetricCard from './panels/MetricCard.vue'
import Graph from './Graph.vue'
import { shortLinksVisible } from '@/utils/shortLinkMetrics'
import { shortLinkUrl, publicationNetworkUrl } from '@/utils/shortLinks'
import MiniDonut from '../overview/modules/widgets/MiniDonut.vue'

// Types de post enregistrés sur les liens (mode de la Lambda de publication) → libellés existants
const TYPE_KEYS = {
  standard: 'dash.reporting.filter.type.link',
  link: 'dash.reporting.filter.type.link',
  image: 'dash.overview.type.image',
  video: 'dash.overview.type.video',
  reel: 'dash.overview.type.reel',
  carousel: 'dash.overview.type.carousel',
  story: 'dash.overview.type.story',
  document: 'dash.overview.type.document'
}

// Clics sur les liens courts mws.li de la page (affichée seulement si le raccourcisseur est actif et qu'il y a des données)
export default {
  name: 'ReportingShortLinks',
  components: { MetricCard, Graph, MiniDonut },
  props: {
    data: { type: Object, default: null },
    page: { type: Object, default: null },
  },
  computed: {
    visible() { return shortLinksVisible(this.data) },
    graphId() { return this.page ? this.page.id : 'page' },
    days() {
      return ((this.data && this.data.by_day) || []).slice().sort((a, b) => (a.day < b.day ? -1 : 1))
    },
    labels() {
      const format = this.$i18n.locale === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
      return this.days.map(d => moment(d.day).format(format))
    },
    graphData() {
      if (!this.days.length) return []
      return [
        { label: this.$t('dash.reporting.short_links.clicks'), data: this.days.map(d => d.clicks || 0), type: 'bar', color: '#6B1F4D' },
        { label: this.$t('dash.reporting.short_links.bot_clicks'), data: this.days.map(d => d.bot_clicks || 0), type: 'line', color: '#9ba3b7' },
      ]
    },
    topLinks() {
      return ((this.data && this.data.top_links) || []).filter(l => l.clicks || l.bot_clicks)
    },
    // Clics humains par type de publication (types de même libellé regroupés : standard / link → Lien)
    typeItems() {
      const byLabel = {}
      ;((this.data && this.data.by_post_type) || []).forEach(row => {
        if (!row.clicks) return
        const label = this.typeLabel(row.post_type)
        byLabel[label] = (byLabel[label] || 0) + row.clicks
      })
      return Object.entries(byLabel).map(([label, value]) => ({ label, value }))
    },
    commentClicks() {
      const row = ((this.data && this.data.by_placement) || []).find(r => r.placement === 'comment')
      return (row && row.clicks) || 0
    },
    commentShare() {
      const total = (this.data && this.data.clicks) || 0
      return total ? Math.round(this.commentClicks / total * 100) : 0
    },
  },
  methods: {
    shortUrl(link) { return shortLinkUrl(link) },
    typeLabel(type) {
      const key = TYPE_KEYS[type]
      return key ? this.$t(key) : this.$t('dash.reporting.short_links.type_other')
    },
    networkUrl(link) { return publicationNetworkUrl(link.publication) },
    publicationTitle(link) {
      const title = (link.publication && link.publication.title) || ''
      return title || this.$t('dash.reporting.short_links.untitled')
    },
    // Même navigation que l'overview (dataLoaders openPost) : section posts2, post en params du router
    openInMashup(link) {
      const pub = link.publication || {}
      localStorage.setItem('section', 'posts2')
      this.$router.push({ name: 'posts2', params: { post_id: String(link.news_id), page_id: String(pub.page_id || link.page_id), bot_id: String(pub.bot_id || '') } })
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }
.section-subtitle { font-size: 12px; color: #9ba3b7; margin: 0 0 16px; }
.metric-row { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 20px; }
.graph-title {
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 16px 0 6px;
}
.graph-wrap { width: 100%; margin-bottom: 8px; }
.sl-table { display: flex; flex-direction: column; font-size: 13px; }
.sl-row {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(130px, 1fr) minmax(110px, 1fr) minmax(0, 2fr) 80px 80px;
  gap: 12px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1edf5;
}
.sl-head { font-size: 11px; font-weight: 600; color: #9ba3b7; text-transform: uppercase; }
.sl-short { color: #6B1F4D; font-weight: 600; white-space: nowrap; }
.sl-dest { color: #4b5563; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sl-pub { color: #121826; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
a.sl-pub:hover { color: #6B1F4D; text-decoration: underline; }
.sl-num { text-align: right; font-variant-numeric: tabular-nums; }
.muted { color: #9ba3b7; }
.sl-type { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; color: #4b5563; }
.sl-badge {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 10px;
  background: #f4ecf1;
  color: #6B1F4D;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.sl-types { display: flex; flex-wrap: wrap; align-items: center; gap: 24px; margin-bottom: 8px; }
.sl-comment-share { margin: 0; font-size: 13px; color: #4b5563; display: flex; align-items: center; gap: 8px; }
</style>
